import React, { memo } from 'react';
import { View, Text } from '@tarojs/components';
import { Card as CardType } from '@/types/game';
import { CARD_SIZE } from '@/data/board';
import SheepSprite, { hasSheepSprite } from '@/components/SheepSprite';
import styles from './index.module.scss';

interface GameCardProps {
  card: CardType;
  onClick?: (cardId: string) => void;
  x?: number;
  y?: number;
  zIndex?: number;
  small?: boolean;
  hidden?: boolean;
  motionTransform?: string;
  motionDisabled?: boolean;
  artHidden?: boolean;
}

const GameCard: React.FC<GameCardProps> = ({ card, onClick, x, y, zIndex, small = false, hidden = false, motionTransform, motionDisabled, artHidden }) => {
  const handleClick = () => {
    if (!hidden && !motionDisabled && ((card.status === 0 && !card.isCover) || card.status === 3) && onClick) {
      onClick(card.id);
    }
  };

  const displayX = x !== undefined ? x : card.x;
  const displayY = y !== undefined ? y : card.y;
  const finalZIndex = zIndex !== undefined ? zIndex + 10 : 1000 - displayY;
  const faceSize = small ? 84 : card.size ?? CARD_SIZE;

  const classList = [styles.card];
  if (card.isCover) classList.push(styles.cover);
  if (small) classList.push(styles.small);
  if (card.status === 1 && !small) classList.push(styles.clicked);
  if (card.status === 2) classList.push(styles.eliminated);

  return (
    <View
      id={'tile-' + card.id}
      data-card-id={card.id}
      data-zone={card.zone}
      data-layer={card.layer}
      data-covered={card.isCover ? 'true' : 'false'}
      className={classList.join(' ')}
      style={small ? {
        opacity: card.status === 2 || hidden ? 0 : 1,
        transform: card.status === 2 ? 'scale(0) rotate(180deg)' : 'scale(1) rotate(0deg)'
      } : {
        left: `${displayX}rpx`,
        top: `${displayY}rpx`,
        width: `${faceSize}rpx`, height: `${faceSize}rpx`,
        opacity: card.status === 2 || hidden ? 0 : 1,
        transform: motionTransform || (card.status === 2 ? 'scale(0)' : undefined),
        zIndex: finalZIndex
      }}
      onClick={handleClick}
    >
      <View className={styles.tileBack}>
        <SheepSprite name='block_bg' size={faceSize * 135 / 120} />
      </View>
      <View className={styles.art} style={{ opacity: artHidden ? 0 : 1 }}>
        {hasSheepSprite(card.type)
          ? <SheepSprite name={card.type} size={small ? 56 : 62 * faceSize / CARD_SIZE} />
          : <Text>{card.emoji}</Text>}
      </View>
      <View className={styles.coverShade} />
    </View>
  );
};

export default memo(GameCard, (before, after) => {
  const a = before.card, b = after.card;
  return a.id === b.id && a.type === b.type && a.emoji === b.emoji && a.status === b.status && a.isCover === b.isCover
    && a.x === b.x && a.y === b.y && a.zIndex === b.zIndex && a.zone === b.zone && a.layer === b.layer && a.size === b.size
    && before.onClick === after.onClick && before.x === after.x && before.y === after.y && before.zIndex === after.zIndex
    && before.small === after.small && before.hidden === after.hidden && before.motionTransform === after.motionTransform
    && before.motionDisabled === after.motionDisabled && before.artHidden === after.artHidden;
});
